'use client'

import { MinusIcon, PlusIcon } from 'lucide-react'
import { useTranslations } from 'next-intl'
import { useState } from 'react'

import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { ACCOUNT_CONNECTION_EMPTY_CLASS } from '@/features/dashboard/components/account-connection/styles'
import { useNumberFormat } from '@/hooks/use-number-format'
import {
  canFundCreate,
  canSubmit,
  consumesSelfSlot,
  createFundingTotal,
  currencyList,
  defaultFundingSubaccount,
  hasKnownUsdcBalance,
  MAX_NAMED_CURRENCIES,
  type SelectionState,
  SERVER_PICKS_FUNDING,
  setCreateCount,
  setFundingSubaccount,
  toggleSubaccount,
  totalSlots,
} from '@/lib/derive/subaccount-selection'
import { cn } from '@/lib/utils'
import { formatCurrency } from '@/lib/utils/format-util'
import type { DeriveSubaccountInfo } from '@/types/generated/public_api'

// Ties the "Change funding account" toggle to the group it opens, so the
// disclosure state is announced rather than only drawn.
const FUNDING_GROUP_ID = 'derive-funding-subaccount-group'

// Fallback cap used only when the server omits maxSlotsPerLink (0). The server
// is authoritative — a single batch link touches at most this many account
// slots (the initiating account plus new siblings).
const DEFAULT_MAX_SLOTS_PER_LINK = 5

const DERIVE_DEPOSIT_URL = 'https://app.derive.xyz/'

/**
 * Shown instead of the picker when the wallet owns no subaccount at all.
 *
 * A subaccount is spawned by transferring spot out of an existing one, so
 * there is nothing to create from until the customer has deposited on
 * derive.xyz. The link handler agrees: with no owned subaccount it answers
 * "Deposit into a Derive subaccount on derive.xyz before adding more" and
 * creates nothing, so the create stepper here could only ever fail.
 */
export function DeriveDepositPrompt({
  onRefresh,
  refreshing,
  isPageLayout = false,
}: {
  onRefresh: () => void
  refreshing: boolean
  isPageLayout?: boolean
}) {
  const t = useTranslations('accountConnection')

  return (
    <div className="border-foreground/15 bg-foreground/8 flex flex-col gap-3 border p-4 text-left">
      <p className="text-sm font-medium">{t('deriveNoSubaccountsTitle')}</p>
      <p
        className={cn(
          'text-xs leading-relaxed text-white/70',
          isPageLayout && 'text-foreground/70'
        )}
      >
        {t('deriveNoSubaccountsBody')}
      </p>
      <a
        href={DERIVE_DEPOSIT_URL}
        target="_blank"
        rel="noreferrer"
        className="text-foreground w-fit text-xs font-medium underline underline-offset-2 hover:opacity-80"
      >
        {t('deriveDepositLink')}
      </a>
      <Button
        variant="basic"
        type="button"
        className="w-fit"
        onClick={onRefresh}
        disabled={refreshing}
      >
        {t('deriveNoSubaccountsRefresh')}
      </Button>
    </div>
  )
}

type DeriveSubaccountPickerProps = {
  /** Never empty — the caller renders `DeriveDepositPrompt` in that case. */
  subaccounts: DeriveSubaccountInfo[]
  /** plan-cap headroom for NEW sibling accounts (excludes the initiating one) */
  remainingSlots: number
  /** true when the initiating account still needs its own subaccount slot */
  initiatingNeedsLink: boolean
  /**
   * Server-authoritative id of the initiating account's own subaccount
   * ('0' = none yet). Re-linking this id consumes the self-slot, not a sibling.
   */
  initiatingSubaccountId: string
  /** The initiating/working account id, used to spot its own "Reconnect" row. */
  workingAccountId: string
  /** Server cap on selections per link request (0 → DEFAULT_MAX_SLOTS_PER_LINK). */
  maxSlotsPerLink: number
  /**
   * USDC each create moves into the new subaccount, from the server's own
   * config. '' when the server did not say, which hides the explainer rather
   * than stating a figure this build guessed.
   */
  createAmountUsd: string
  selection: SelectionState
  onChange: (selection: SelectionState) => void
  onSubmit: () => void
  submitting: boolean
  isPageLayout?: boolean
}

function Pill({
  children,
  isPageLayout,
  title,
}: {
  children: React.ReactNode
  isPageLayout: boolean
  title?: string
}) {
  return (
    <span
      title={title}
      className={cn(
        'shrink-0 px-1.5 py-0.5 text-[10px] font-medium tracking-tight uppercase',
        'bg-white/10 text-white/70',
        isPageLayout && 'bg-foreground/10 text-foreground/70'
      )}
    >
      {children}
    </span>
  )
}

export function DeriveSubaccountPicker({
  subaccounts,
  remainingSlots,
  initiatingNeedsLink,
  initiatingSubaccountId,
  workingAccountId,
  maxSlotsPerLink,
  createAmountUsd,
  selection,
  onChange,
  onSubmit,
  submitting,
  isPageLayout = false,
}: DeriveSubaccountPickerProps) {
  const t = useTranslations('accountConnection')
  const fmt = useNumberFormat()
  const [fundingOpen, setFundingOpen] = useState(false)

  // Server-authoritative per-link cap, falling back only when unset.
  const perLinkCap = maxSlotsPerLink > 0 ? maxSlotsPerLink : DEFAULT_MAX_SLOTS_PER_LINK
  // The initiating account is allowed one slot on top of the sibling headroom
  // — either because it needs its first subaccount or because the user picked
  // its own subaccount to re-link (both are `consumesSelfSlot`).
  const used = totalSlots(selection)
  const selfSlotUsed = consumesSelfSlot(selection, initiatingSubaccountId)
  const maxSlots = Math.min(perLinkCap, remainingSlots + (selfSlotUsed ? 1 : 0))
  // The initiating account's own re-link never spends sibling headroom, so its
  // row stays selectable up to the absolute per-link cap even when siblings are
  // full (before it's picked, `selfSlotUsed` is still false).
  const reconnectMaxSlots = Math.min(perLinkCap, remainingSlots + 1)
  const atCapacity = used >= maxSlots
  // Every create draws from the same subaccount, so the source has to cover
  // the whole batch, not one transfer. The server checks the same total.
  const fundingTotalUsd = createFundingTotal(createAmountUsd, selection.createCount)
  // What the create will actually draw from — the user's own pick, or the
  // subaccount the server would choose, computed the same way it does so the
  // stated source is the real one rather than a guess.
  const fundingSourceId =
    selection.fundingSubaccountId !== SERVER_PICKS_FUNDING
      ? selection.fundingSubaccountId
      : defaultFundingSubaccount(subaccounts, fundingTotalUsd)
  // A create with no source is refused request-wide, taking the existing
  // subaccounts the user also ticked down with it, so the button is held
  // rather than letting them lose links they could have had. Held only when
  // every balance is known: an enrichment gap must not lock the flow, since
  // the server re-prices at submit and can find a source the picker could not.
  const noFundingSource =
    selection.createCount > 0 &&
    fundingSourceId === '' &&
    subaccounts.every((sub) => hasKnownUsdcBalance(sub.usdcBalance))
  const submittable =
    canSubmit(selection, remainingSlots, initiatingSubaccountId) && !noFundingSource

  const stepperButtonClass = cn(
    'flex size-7 items-center justify-center border transition-colors disabled:opacity-40 not-disabled:cursor-pointer',
    'border-white/10 bg-white/5 not-disabled:hover:bg-white/10',
    isPageLayout && 'border-foreground/10 bg-foreground/5 not-disabled:hover:bg-foreground/10'
  )

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-col gap-2">
        {subaccounts.map((sub) => {
          const checked = selection.selected.includes(sub.subaccountId)
          // The initiating account's own linked subaccount is re-selectable
          // ("Reconnect"); every OTHER account's linked row stays locked.
          const isReconnectRow =
            !initiatingNeedsLink &&
            sub.alreadyLinked &&
            !!sub.linkedAccountId &&
            sub.linkedAccountId === workingAccountId
          const linkedElsewhere = sub.alreadyLinked && !isReconnectRow
          const rowMaxSlots = isReconnectRow ? reconnectMaxSlots : maxSlots
          const disabled = linkedElsewhere || (!checked && used >= rowMaxSlots)
          const currencies = currencyList(sub.currency)
          return (
            <label
              key={sub.subaccountId}
              className={cn(
                'flex items-center gap-3 border border-white/10 bg-white/5 p-3 text-left',
                isPageLayout && 'border-foreground/10 bg-foreground/5',
                disabled ? 'opacity-60' : 'cursor-pointer'
              )}
            >
              <Checkbox
                checked={checked}
                disabled={disabled}
                onCheckedChange={() =>
                  onChange(
                    toggleSubaccount(
                      selection,
                      sub.subaccountId,
                      { alreadyLinked: linkedElsewhere },
                      rowMaxSlots
                    )
                  )
                }
              />
              <div className="flex min-w-0 flex-1 flex-col gap-1">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="truncate text-sm font-medium">
                    {t('deriveSubaccountLabel', { id: sub.subaccountId })}
                  </span>
                  {currencies.length > 0 && (
                    <Pill isPageLayout={isPageLayout} title={currencies.join(', ')}>
                      {currencies.length > MAX_NAMED_CURRENCIES
                        ? t('deriveSubaccountAssetCount', { count: currencies.length })
                        : currencies.join(', ')}
                    </Pill>
                  )}
                  {sub.marginType && <Pill isPageLayout={isPageLayout}>{sub.marginType}</Pill>}
                  {sub.alreadyLinked && (
                    <span className="border-foreground/15 bg-foreground/8 text-foreground shrink-0 border px-2 py-0.5 text-[10px] font-medium">
                      {isReconnectRow
                        ? t('deriveSubaccountReconnect')
                        : t('deriveSubaccountConnected')}
                    </span>
                  )}
                </div>
                {linkedElsewhere && sub.linkedAccountId && (
                  <span
                    className={cn('text-xs text-white/50', isPageLayout && 'text-foreground/50')}
                  >
                    {t('deriveSubaccountLinkedTo', { account: sub.linkedAccountId })}
                  </span>
                )}
              </div>
              <span
                className={cn(
                  'shrink-0 text-sm text-white/80 tabular-nums',
                  isPageLayout && 'text-foreground/80'
                )}
              >
                {sub.balanceUsd
                  ? fmt.text(formatCurrency(sub.balanceUsd, true), { sensitive: true })
                  : '—'}
              </span>
            </label>
          )
        })}
      </div>

      <div
        className={cn(
          'flex items-center justify-between gap-3 border border-white/10 bg-white/5 p-3',
          isPageLayout && 'border-foreground/10 bg-foreground/5'
        )}
      >
        <span className="text-sm font-medium">{t('deriveCreateNewLabel')}</span>
        <div className="flex items-center gap-3">
          <button
            type="button"
            className={stepperButtonClass}
            onClick={() => onChange(setCreateCount(selection, selection.createCount - 1, maxSlots))}
            disabled={selection.createCount <= 0}
            aria-label={t('deriveDecreaseSubaccounts')}
          >
            <MinusIcon className="size-3.5" />
          </button>
          <span className="w-4 text-center text-sm tabular-nums">{selection.createCount}</span>
          <button
            type="button"
            className={stepperButtonClass}
            onClick={() => onChange(setCreateCount(selection, selection.createCount + 1, maxSlots))}
            disabled={atCapacity}
            aria-label={t('deriveIncreaseSubaccounts')}
          >
            <PlusIcon className="size-3.5" />
          </button>
        </div>
      </div>

      {selection.createCount > 0 && createAmountUsd !== '' && (
        <div
          className={cn(
            'flex flex-col gap-2 border border-white/10 bg-white/5 p-3',
            isPageLayout && 'border-foreground/10 bg-foreground/5'
          )}
        >
          <p
            className={cn(
              'text-xs leading-relaxed text-white/70',
              isPageLayout && 'text-foreground/70'
            )}
          >
            {t('deriveCreateFundingExplainer', {
              amount: createAmountUsd,
              total: fundingTotalUsd,
              count: selection.createCount,
            })}
          </p>
          <div className="flex flex-wrap items-center gap-2">
            <span className={cn('text-xs text-white/70', isPageLayout && 'text-foreground/70')}>
              {fundingSourceId === ''
                ? t('deriveCreateNoFundingSource', { amount: fundingTotalUsd })
                : t('deriveCreateFundedFrom', { id: fundingSourceId })}
            </span>
            <button
              type="button"
              className="text-foreground cursor-pointer text-xs font-medium underline underline-offset-2 hover:opacity-80"
              aria-expanded={fundingOpen}
              aria-controls={FUNDING_GROUP_ID}
              onClick={() => setFundingOpen((open) => !open)}
            >
              {t('deriveChangeFundingAccount')}
            </button>
          </div>
          {fundingOpen && (
            <fieldset
              id={FUNDING_GROUP_ID}
              className="flex flex-col gap-1 border-0 p-0"
              aria-label={t('deriveChangeFundingAccount')}
            >
              {/* Automatic is a radio, not a button beside the group: it is the
                  state a fresh selection is in, and a group where nothing reads
                  as checked tells a screen-reader user nothing is chosen. */}
              <label className="flex cursor-pointer items-center gap-2 px-2 py-1.5 text-xs">
                <input
                  type="radio"
                  name="derive-funding-subaccount"
                  className="accent-foreground"
                  value={SERVER_PICKS_FUNDING}
                  checked={selection.fundingSubaccountId === SERVER_PICKS_FUNDING}
                  onChange={() => onChange(setFundingSubaccount(selection, SERVER_PICKS_FUNDING))}
                />
                <span className="flex-1 truncate">{t('deriveFundingAutomatic')}</span>
              </label>
              {subaccounts.map((sub) => {
                // An unknown balance stays selectable: the server re-prices
                // every subaccount at submit and answers by name if the pick
                // cannot cover the transfer, which beats greying out a
                // subaccount that may hold plenty.
                const known = hasKnownUsdcBalance(sub.usdcBalance)
                const selectable = !known || canFundCreate(sub.usdcBalance, fundingTotalUsd)
                return (
                  <label
                    key={sub.subaccountId}
                    className={cn(
                      'flex items-center gap-2 px-2 py-1.5 text-xs',
                      selectable ? 'cursor-pointer' : 'opacity-50'
                    )}
                  >
                    <input
                      type="radio"
                      name="derive-funding-subaccount"
                      className="accent-foreground"
                      value={sub.subaccountId}
                      checked={selection.fundingSubaccountId === sub.subaccountId}
                      disabled={!selectable}
                      onChange={() => onChange(setFundingSubaccount(selection, sub.subaccountId))}
                    />
                    <span className="flex-1 truncate">
                      {t('deriveSubaccountLabel', { id: sub.subaccountId })}
                    </span>
                    <span className="tabular-nums">
                      {known ? t('deriveFundingUsdcAvailable', { usdc: sub.usdcBalance }) : '—'}
                    </span>
                  </label>
                )
              })}
            </fieldset>
          )}
        </div>
      )}

      <p className={cn('text-center text-xs text-white/50', isPageLayout && 'text-foreground/50')}>
        {t('deriveSlotsMeter', { used, max: maxSlots })}
      </p>

      <p className={cn('text-left text-xs text-white/50', isPageLayout && 'text-foreground/50')}>
        {t('deriveAuthorizeScopeNote')}
      </p>

      <Button
        variant="basic"
        type="button"
        className="mx-auto w-fit"
        onClick={onSubmit}
        disabled={!submittable || submitting}
      >
        {submitting ? t('deriveLinking') : t('deriveLinkButton')}
      </Button>
    </div>
  )
}
